import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import { CreateBookingInput } from "@/lib/booking.schema";
import { attachProfilesByUserId } from "@/lib/admin-profiles";

const ADMIN_BOOKING_SELECT =
  "id, start_at, end_at, status, student_name, student_age, phone, notes, user_id, member_package_id, lesson_types(name, duration_minutes), member_packages(label, sessions_total, sessions_used)";

export const getAdminSchedule = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ start: z.string(), end: z.string() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const [bookingsRes, blocksRes, eventsRes] = await Promise.all([
      supabase
        .from("bookings")
        .select(ADMIN_BOOKING_SELECT)
        .gte("start_at", data.start)
        .lt("start_at", data.end)
        .order("start_at", { ascending: true }),
      supabase
        .from("time_blocks")
        .select("*")
        .lt("start_at", data.end)
        .gt("end_at", data.start)
        .order("start_at", { ascending: true }),
      supabase
        .from("site_events")
        .select("id, title, date_label, audience, details, start_at, end_at, capacity, published")
        .lt("start_at", data.end)
        .gt("end_at", data.start)
        .order("start_at", { ascending: true }),
    ]);
    if (bookingsRes.error) throw new Error(bookingsRes.error.message);
    if (blocksRes.error) throw new Error(blocksRes.error.message);
    if (eventsRes.error) throw new Error(eventsRes.error.message);

    const bookings = await attachProfilesByUserId(supabase, bookingsRes.data ?? [], "user_id");

    const rawEvents = eventsRes.data ?? [];
    const { countConfirmedRegistrationsByEventIds } =
      await import("@/lib/event-registration.server");
    const eventCountMap = await countConfirmedRegistrationsByEventIds(
      supabase,
      rawEvents.map((e) => e.id),
    );

    const events = rawEvents.map((e) => ({
      ...e,
      confirmed_count: eventCountMap.get(e.id) ?? 0,
    }));

    return { bookings, blocks: blocksRes.data ?? [], events };
  });

export const listPendingBookings = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("bookings")
      .select(ADMIN_BOOKING_SELECT)
      .eq("status", "pending")
      .order("start_at", { ascending: true });
    if (error) throw new Error(error.message);
    return attachProfilesByUserId(supabase, data ?? [], "user_id");
  });

export const updateBookingStatus = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["pending", "confirmed", "cancelled", "completed"]),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { transitionBookingStatus } = await import("@/lib/booking.server");
    return transitionBookingStatus({
      bookingId: data.id,
      toStatus: data.status,
      supabase: context.supabase,
    });
  });

const AdminCreateBookingInput = CreateBookingInput.extend({
  userId: z.string().uuid(),
});

export const adminCreateBooking = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => AdminCreateBookingInput.parse(d))
  .handler(async ({ data }) => {
    const { createBookingCore } = await import("@/lib/booking.server");
    const { userId, ...booking } = data;
    return createBookingCore(userId, booking, { skipCutoff: true, confirmOnCreate: true });
  });

const AdminUpdateBookingDetailsInput = z.object({
  id: z.string().uuid(),
  studentName: z.string().trim().min(1).max(100),
  phone: z.string().trim().min(7).max(30),
  studentAge: z.number().int().min(3).max(120).optional().nullable(),
  notes: z.string().trim().max(1000).optional().nullable(),
});

export const adminUpdateBookingDetails = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => AdminUpdateBookingDetailsInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { id, studentName, phone, studentAge, notes } = data;

    const { data: booking, error: fetchErr } = await supabase
      .from("bookings")
      .select("id, user_id")
      .eq("id", id)
      .maybeSingle();
    if (fetchErr) throw new Error(fetchErr.message);
    if (!booking) throw new Error("Booking not found");

    // Service role write: authenticated UPDATE on bookings is revoked.
    const { error } = await supabaseAdmin
      .from("bookings")
      .update({
        student_name: studentName,
        phone,
        student_age: studentAge ?? null,
        notes: notes ?? null,
      })
      .eq("id", id);
    if (error) throw new Error(error.message);

    const { syncBookingContactToProfile } = await import("@/lib/booking.server");
    await syncBookingContactToProfile(supabaseAdmin, booking.user_id, {
      fullName: studentName,
      phone,
    });

    return { ok: true };
  });

const AdminSlotInput = z.object({
  date: z.string(),
  lessonTypeId: z.string().uuid(),
});

const AdminBookableDatesInput = z.object({
  start: z.string(),
  end: z.string(),
  lessonTypeId: z.string().uuid(),
});

/** Admin-only availability that bypasses the public booking cutoff. */
export const getAdminAvailableSlots = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => AdminSlotInput.parse(d))
  .handler(async ({ data }) => {
    const { loadAvailabilitySnapshot } = await import("@/lib/availability-loader.server");
    const { slotsForDate } = await import("@/lib/availability-loader");
    const snapshot = await loadAvailabilitySnapshot(data.date, data.date, data.lessonTypeId);
    if (!snapshot) return [];
    return slotsForDate(snapshot, data.date, { skipCutoff: true });
  });

/** Admin-only bookable dates that bypass the public booking cutoff. */
export const getAdminBookableDates = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => AdminBookableDatesInput.parse(d))
  .handler(async ({ data }) => {
    const { loadAvailabilitySnapshot } = await import("@/lib/availability-loader.server");
    const { bookableDateKeys } = await import("@/lib/availability-loader");
    const snapshot = await loadAvailabilitySnapshot(data.start, data.end, data.lessonTypeId);
    if (!snapshot) return [];
    return bookableDateKeys(snapshot, data.start, data.end, { skipCutoff: true });
  });
